import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {
  LabelPickerBase,
  type CreateLabelMutationCallback,
  type LabelPickerBaseProps,
  type LabelPickerLabel as LabelPickerBaseLabel,
} from '@github-ui/item-picker-label'
import {QueryClient, useQueryClient, type QueryPersister} from '@github-ui/react-query'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  apiGetSearchLabels,
  buildItemPickerLabelSearchQueryKey,
  transformSearchLabelResponseToQueryData,
  type LabelPickerQueryData,
} from './api/api-get-search-labels'
import {parseGraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {createLabelPersister} from './api/label-persister'
import {useCreateLabelMutation} from './mutations/use-create-label-mutation'

//
// LabelPickerBase <== ItemPickerFetcher (internal) <== LabelPicker (exported)
//                                                  <== BulkIssueLabelPicker (exported)

type LabelQueryKey = ReturnType<typeof buildItemPickerLabelSearchQueryKey>

export async function fetchLabels(
  queryClient: QueryClient,
  {
    owner,
    repo,
    query,
    count,
    labelNames,
    suggested,
    persister,
  }: {
    owner: string
    repo: string
    query: string
    count?: number
    labelNames?: string[]
    suggested?: boolean
    persister?: QueryPersister<LabelPickerQueryData, LabelQueryKey>
  },
): Promise<LabelPickerQueryData> {
  const response = await queryClient.fetchQuery({
    queryKey: buildItemPickerLabelSearchQueryKey({owner, repo, query, count, labelNames, suggested}),
    queryFn: async () => {
      const responseJson = await apiGetSearchLabels({owner, repo, query, count, labelNames, suggested})
      const responseData = parseGraphQLResponse(responseJson)
      return transformSearchLabelResponseToQueryData(responseData.data, labelNames)
    },
    persister,
  })
  return response
}

export type LabelPickerLabel = LabelPickerBaseLabel

export type LabelPickerProps = Omit<LabelPickerBaseProps, 'filterItems'> & {
  labelNames?: string[]
  /**
   * When true, the fetch made when the picker opens (before any search term is
   * typed) asks the server for the popularity-ranked "Suggested labels" group
   * and renders it at the top of the picker. The server flag-gates the data, so
   * this is safe to enable on the apply surfaces (issue/PR sidebar, new-issue
   * form) without a client flag.
   */
  enableSuggestedLabels?: boolean
}

function ItemPickerFetcher({
  repo,
  owner,
  labels,
  labelNames,
  noLabelOption,
  enableSuggestedLabels,
  ...rest
}: LabelPickerProps) {
  // Initialize state based on preloaded data availability
  const [isLoading, setIsLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<LabelPickerQueryData | null>(null)

  const [initialLabels, setInitialLabels] = useState<LabelPickerLabel[]>(labels)

  const queryClient = useQueryClient()
  const {mutate: createLabelMutate} = useCreateLabelMutation()
  const {sendAnalyticsEvent} = useAnalytics()

  // Read the latest `labelNames` inside the initial-load effect without adding
  // it to that effect's dependency array. Depending on `labelNames` there would
  // re-run the suggestions request on every selection toggle, and the server
  // drops the popularity-ranked "Suggested labels" group whenever the request
  // carries `labelNames` — so the suggestions would disappear on each toggle.
  const labelNamesRef = useRef(labelNames)
  useEffect(() => {
    labelNamesRef.current = labelNames
  }, [labelNames])

  // Tracks whether the preselected labels supplied by name have been resolved at
  // least once. The initial resolution is awaited by the load effect below so
  // the picker does not commit an empty selection while the URL still carries
  // label filters; later re-resolutions (triggered by toggles rewriting the URL)
  // are handled non-blockingly by the effect further down.
  const initialSelectionResolvedRef = useRef(false)

  useEffect(() => {
    const fetchInitialLabels = async () => {
      // Fetch the most recently updated labels in alphabetical order
      try {
        setIsLoading(true)

        const persister: QueryPersister<LabelPickerQueryData, LabelQueryKey> = async (queryFn, context, query) => {
          const labelPersister = createLabelPersister(sendAnalyticsEvent)
          return labelPersister(queryFn, context, query)
        }

        // Resolve labels supplied by name only (e.g. the issues list label filter
        // reads its selected labels from the URL query) alongside the suggestions
        // request. Both run as independent requests — the server only embeds the
        // popularity-ranked "Suggested labels" group when the request carries no
        // `labelNames` — but the picker must not render until BOTH complete: if
        // the selected-label resolution failed silently, `onSelectionChanged`
        // would fire without the preselected labels and `replaceTokensDifferentially`
        // would strip the still-present URL filters. Awaiting them together keeps
        // the picker loading and routes either failure through the retryable
        // error state below.
        const labelNamesToResolve = labelNamesRef.current
        const [response, selectedResponse] = await Promise.all([
          fetchLabels(queryClient, {
            owner,
            repo,
            query: '',
            count: 1000,
            suggested: enableSuggestedLabels,
            persister,
          }),
          labelNamesToResolve && labelNamesToResolve.length > 0
            ? fetchLabels(queryClient, {owner, repo, query: '', count: 1000, labelNames: labelNamesToResolve})
            : Promise.resolve(null),
        ])
        setData(response)
        if (selectedResponse) {
          setInitialLabels(selectedResponse.labelsByNames || [])
        }
        initialSelectionResolvedRef.current = true

        setIsLoading(false)
        setIsError(false)
      } catch {
        setIsError(true)
        setIsLoading(false)
      }
    }
    // This useEffect populates the initial list of labels to show in the picker.
    // It does not take a query into account and only relies on owner / repo. It
    // reads `labelNames` via a ref rather than depending on it (see the ref
    // declaration above). Retrying via `fetchKey` reruns both the suggestions and
    // the preselected-label requests.
    fetchInitialLabels()
  }, [fetchKey, owner, queryClient, repo, enableSuggestedLabels, sendAnalyticsEvent])

  // Re-resolve preselected labels after the initial load whenever `labelNames`
  // changes (a selection toggle rewrites the URL, which flows back in as new
  // `labelNames`). This is intentionally non-blocking: the picker is already
  // rendered with a valid selection, so a slow or failed re-resolution must not
  // flash it back into a loading state or tear it down. The initial resolution
  // is handled by the load effect above, so this effect skips until that has run.
  useEffect(() => {
    if (!initialSelectionResolvedRef.current) {
      return
    }
    if (!labelNames || labelNames.length === 0) {
      return
    }

    // `ignore` guards against a stale response overwriting newer state: if
    // `labelNames` changes again (or the picker unmounts) before this request
    // resolves, the cleanup flips the flag so the superseded response is ignored
    // and we never call setState after unmount.
    let ignore = false
    const resolveSelectedLabels = async () => {
      try {
        const response = await fetchLabels(queryClient, {owner, repo, query: '', count: 1000, labelNames})
        if (!ignore) {
          setInitialLabels(response.labelsByNames || [])
        }
      } catch {
        // Non-fatal: the "Selected" group may be briefly stale, but the picker
        // already holds a valid selection from the initial load, so the rest of
        // the picker (suggestions, list, search) keeps working.
      }
    }
    resolveSelectedLabels()

    return () => {
      ignore = true
    }
  }, [labelNames, owner, queryClient, repo])

  const anchorRef = useRef<HTMLButtonElement>(null)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('labels'),
    anchorElement: anchorProps => rest.anchorElement(anchorProps, anchorRef),
    open: true,
  })

  const fetchLabelsWithQueryClient = useCallback(
    async (query: string) => {
      const response = await fetchLabels(queryClient, {owner, repo, query})
      return response.labels
    },
    [queryClient, owner, repo],
  )

  const onCreate: CreateLabelMutationCallback = useCallback(
    (input, {onSuccess, onError}) => {
      createLabelMutate(input, {
        onSuccess,
        onError,
      })
    },
    [createLabelMutate],
  )

  // Handle noLabelOption for suggestions (add to the beginning of the list)
  const suggestions = useMemo(() => {
    const suggestionsList = data?.labels ?? []
    if (noLabelOption) {
      return [SPECIAL_VALUES.noLabelsData as LabelPickerLabel, ...suggestionsList]
    }
    return suggestionsList
  }, [data?.labels, noLabelOption])

  // Handle noLabelOption for selected labels (add when noLabelOption.selected is true)
  const allLabels = useMemo(() => {
    const labelsList = labelNames ? initialLabels : labels
    if (noLabelOption?.selected && !labelsList.find(l => l.id === SPECIAL_VALUES.noLabelsData.id)) {
      return [SPECIAL_VALUES.noLabelsData as LabelPickerLabel, ...labelsList]
    }
    return labelsList
  }, [initialLabels, labelNames, labels, noLabelOption?.selected])

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  // Only present the combined "Suggestions" experience once the server has
  // actually returned the popularity-ranked field. An older or flag-disabled
  // server omits `suggestedLabels`, and that omission must remain the
  // backwards-compatible "disabled" signal so favorites keep their own group.
  const showSuggestedLabels = Boolean(enableSuggestedLabels) && data?.suggestedLabels !== undefined

  return (
    <LabelPickerBase
      repo={repo}
      owner={owner}
      repositoryId={data?.repositoryId ?? ''}
      labels={allLabels}
      suggestions={suggestions}
      popularLabels={data?.suggestedLabels}
      enableSuggestedLabels={showSuggestedLabels}
      totalNumberOfLabels={data?.totalCount ?? 0}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      searchLabels={fetchLabelsWithQueryClient}
      onCreate={onCreate}
      noLabelOption={noLabelOption}
      {...rest}
    />
  )
}

export function LabelPicker({
  repo,
  owner,
  anchorElement,
  readonly,
  labels,
  labelNames,
  noLabelOption,
  ...rest
}: LabelPickerProps) {
  return (
    <LazyItemPicker
      keybindingCommandId="item-pickers:open-labels"
      anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
      createChild={() => (
        <ItemPickerFetcher
          repo={repo}
          owner={owner}
          readonly={readonly}
          initialFilter={''}
          labels={labels}
          labelNames={labelNames}
          triggerOpen
          anchorElement={anchorElement}
          noLabelOption={noLabelOption}
          {...rest}
        />
      )}
      insidePortal={rest.insidePortal}
    />
  )
}
